import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { useFrontChatControl } from '@features/front-chat';
import { CleanupWizardEntry } from '@features/practice-cleanup';
import { useCan, useOrganizationId } from '@features/user';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TuneIcon from '@mui/icons-material/Tune';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useModal } from '@store/hooks';

import { AppointmentTypeCatalogContainer } from './AppointmentTypeCatalogContainer';

/**
 * Full-height route for the evergreen catalog (GDN-2335):
 * /practice/appointment-types/catalog. A data-dense workbench wants a
 * bounded, full-width viewport — the /practice tab container (scrolling,
 * maxWidth lg) can't provide one, so this route lives OUTSIDE PracticeLayout,
 * with a back link to the overview tab. Height follows the RuleBuildPage
 * convention (viewport minus the app nav), which lets the catalog's flex
 * column pin its footer to the bottom.
 *
 * Header actions live here (not in the catalog's filter bar): Manage opens
 * the org management modal; the cleanup wizard launches via its own entry
 * (button variant) — the catalog is where admins SEE the duplicates, so
 * it's where the wizard should be offered.
 */
export const AppointmentTypeCatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const isEnabled = useFeatureGate(FeatureGates.PRACTICE_TYPE_CATALOG);
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const organizationId = useOrganizationId() ?? undefined;
  const { typeCatalog } = useEventTracking();
  const { open: openModal } = useModal();
  const { hideLauncher, showLauncher } = useFrontChatControl();

  // The Front chat FAB sits bottom-left — exactly over the footer's
  // selection summary. Hide it for the workbench, restore on leave.
  useEffect(() => {
    hideLauncher();
    return (): void => {
      showLauncher();
    };
  }, [hideLauncher, showLauncher]);

  const handleBack = useCallback((): void => {
    navigate('/practice/appointment-types');
  }, [navigate]);

  const handleManageTypes = useCallback((): void => {
    typeCatalog.manageTypesClicked({ organization_id: organizationId });
    openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
      source: 'practice_type_catalog',
      maxWidth: 'md',
      fullScreenOnMobile: true,
    });
  }, [organizationId, typeCatalog, openModal]);

  // No auto-redirect on a false gate: Statsig may still be initializing on a
  // hard reload, and bouncing a legitimate user is worse than a static
  // fallback with a way back.
  if (!isEnabled || !canManageAppointmentTypes) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Back
            </Button>
          }
        >
          {canManageAppointmentTypes
            ? 'The appointment-type catalog is not enabled for your practice.'
            : 'You do not have permission to manage practice appointment types.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        height: {
          xs: `calc(100vh - ${theme.layout.navHeight.xs}px)`,
          sm: `calc(100vh - ${theme.layout.navHeight.sm}px)`,
        },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      })}
    >
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 2,
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ ml: -1 }}
          >
            Appointment types
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
            Appointment-type catalog
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <CleanupWizardEntry variant="button" />
          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            onClick={handleManageTypes}
          >
            Manage appointment types
          </Button>
        </Stack>
      </Box>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, px: 3, pb: 2.5 }}>
        <AppointmentTypeCatalogContainer />
      </Box>
    </Box>
  );
};

export default AppointmentTypeCatalogPage;
